import { ConnectorHosting } from "@p0security/integrations-app-shared/components";
import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

import { findSecretNameProblem } from "./secret-name.ts";

/** GitHub's rule for an organization login: letters, digits and hyphens, not
 * starting with a hyphen, at most 39 characters. The connector refuses any
 * other org. */
export const GITHUB_ORG_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]{0,38}$/;

export const INVALID_ORG_LOGIN =
  "Enter only the organization's GitHub login, not its URL. For github.com/acme, enter acme.";

/** A GitHub App's ID, the number on the app's settings page. */
export const GITHUB_APP_ID_PATTERN = /^\d+$/;

export const INVALID_APP_ID =
  "The GitHub App ID is a number. Find it on the app's settings page.";

/** Refuses a Google vault or Google hosting. The form hides both options, but
 * a request can still name them. */
export const AWS_ONLY =
  "GitHub Repositories supports only AWS for now: store the key in AWS Secrets Manager and run the connector on AWS Lambda.";

const allVaults = withoutAzureKeyVault(
  newVaultElement({ owner: "integration" })
);

/** Where the GitHub App's private key is vaulted. The connector runs in the
 * vault's cloud, but where in it is `hosting`'s to say.
 *
 * Only AWS Secrets Manager for now (CUS-1059 adds Google Secret Manager), so
 * the Google option is hidden. */
export const RepositoryAccessVault = Element({
  ...allVaults,
  default: "aws-sm",
  options: {
    ...allVaults.options,
    "gcp-sm": { ...allVaults.options["gcp-sm"], hidden: true },
  },
});

/** Where the connector runs. Only AWS Lambda for now (CUS-1059 adds Cloud
 * Run), so the Google option is hidden. */
export const RepositoryAccessHosting = Element({
  ...ConnectorHosting,
  default: "aws",
  options: {
    ...ConnectorHosting.options,
    gcp: { ...ConnectorHosting.options.gcp, hidden: true },
  },
});

export const GithubRepositoriesComponents = InstallSpec({
  // One item per GitHub organization, keyed by the org's login. Each org has
  // its own GitHub App and its own connector deployment.
  //
  // The backend checks the login (`GITHUB_ORG_PATTERN`) rather than an item
  // validator here: the install framework runs that validator first, and its
  // error repeats the whole request, which may hold a key pasted in place of
  // its secret's name.
  "repository-access": ItemComponent({
    label: "Repository access",
    description:
      "Allow P0 to grant GitHub repository roles through a connector you host",
    type: "string",
    maturity: "beta",
    featureFlag: "enable-github-legacy-integrations",
    // Where the key lives, the key's secret and where the connector runs are
    // fixed when the item is created: the connector's Terraform grants it read
    // on exactly that secret. A rotated key goes into the same secret, as a new
    // version. The app can change in place, for a new app, and the next install
    // check validates it.
    schema: {
      appId: Element({
        label: "GitHub App ID",
        description: "ID of the GitHub App you created for repository access",
        type: "string",
        validator: async (_id, appId) =>
          GITHUB_APP_ID_PATTERN.test(appId.trim()) ? undefined : INVALID_APP_ID,
      }),
      vault: RepositoryAccessVault,
      // P0 points the connector at the secret by its partial ARN, in the
      // vault's account and region (`toSecretPartialArn`).
      privateKeySecretName: Element({
        label: "Private key secret",
        description:
          "The name of the secret that holds the GitHub App's private key, in the vault's account and region. P0 never reads it; only your connector does.",
        type: "string",
        step: "new",
        validator: async (_id, name) => findSecretNameProblem(name),
      }),
      hosting: RepositoryAccessHosting,
    },
  }),
});
